import { Box, Fade, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { Control, Controller, FieldErrors, UseFormWatch } from 'react-hook-form';
import GroupRadioButton from '../../components/input/GroupRadioButton';
import { RentCoverageTypes, SecurityDepositTypes } from '../../constants/CoverageType';
import { CurrencyFixedDecimalInput } from '@/shared/formatters';
import { Renter } from '../../utils/types/renter.interface';
import { InviteSegmentEvents } from './constants';
import { useInviteTracking } from './hooks';

type RadioInputBox = {
  id: string;
  text: string;
  value: string;
};

const rentOptions: RadioInputBox[] = [
  {
    id: 'rent-coverage-company-default',
    text: "My company's default",
    value: RentCoverageTypes.CompanyDefault,
  },
  {
    id: 'rent-coverage-customize',
    text: 'Customize Rent Coverage',
    value: RentCoverageTypes.Customize,
  },
];

const securityDepositOptions: RadioInputBox[] = [
  {
    id: 'security-deposit-coverage-company-default',
    text: "My company's default",
    value: SecurityDepositTypes.CompanyDefault,
  },
  {
    id: 'security-deposit-coverage-customize-or-remove',
    text: 'Customize or remove Deposit Coverage',
    value: SecurityDepositTypes.CustomizeOrRemove,
  },
];

interface CoverageSelectionProps {
  control: Control<Renter, any>;
  watch: UseFormWatch<Renter>;
  errors: FieldErrors<Renter>;
}

const CoverageSelection = ({ control, watch, errors }: CoverageSelectionProps) => {
  const seeRentCoverage = watch('rentCoverage');
  const seeSecurityDepositCoverage = watch('securityDepositCoverage');
  const address = watch('address');
  const { trackInvite } = useInviteTracking();

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography mb={1} variant="h5">
          Coverage selection
        </Typography>
        <Typography mb={4}>
          You may set coverage requirements that apply to this deal only, rather than relying on
          your company's default settings.
        </Typography>
        <Typography variant="h6" mb={1}>
          Rent Coverage
        </Typography>
        <Controller
          control={control}
          name="rentCoverage"
          render={({ field }) => (
            <GroupRadioButton
              field={{
                ...field,
                onChange: (event: { target: { value: string } }) => {
                  const selectedValue = event.target.value;
                  field.onChange(selectedValue);
                  if (selectedValue === RentCoverageTypes.Customize) {
                    trackInvite(InviteSegmentEvents.RentCoverageSelected, {
                      property_id: address?.property_id,
                      address_id: address?.address_id,
                      value: selectedValue,
                    });
                  }
                },
              }}
              id="rent-coverage"
              dataTestId="rent-coverage"
              options={rentOptions}
            />
          )}
        />
        {seeRentCoverage === RentCoverageTypes.Customize && (
          <Fade in={seeRentCoverage === RentCoverageTypes.Customize}>
            <Box>
              <Controller
                control={control}
                name="monthsOfCoverage"
                defaultValue={undefined}
                render={({ field }) => (
                  <TextField
                    label="Months of coverage"
                    id="months-of-coverage"
                    type="number"
                    slotProps={{
                      htmlInput: {
                        onWheel: (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
                      },
                    }}
                    {...field}
                    error={!!errors.monthsOfCoverage}
                    helperText={
                      (errors.monthsOfCoverage && errors.monthsOfCoverage?.message?.toString()) ??
                      ''
                    }
                    required
                  />
                )}
              />
              <Stack
                height={100}
                mb={3}
                direction="column"
                px={1.75}
                justifyContent={'space-between'}
              >
                <Typography variant="helpertext">
                  Please enter the number of months you'd like covered excluding prepaid rent,
                  concessions and security deposit.
                </Typography>
                <Typography variant="helpertext">
                  Example: If the tenant has a 12 month lease with 1 month free, and the 1st month
                  paid at lease signing, enter “10” for full coverage
                </Typography>
              </Stack>
            </Box>
          </Fade>
        )}
      </Box>
      <Box>
        <Typography variant="h6" mb={1}>
          Security Deposit Coverage
        </Typography>
        <Controller
          name="securityDepositCoverage"
          control={control}
          render={({ field }) => (
            <GroupRadioButton
              field={{
                ...field,
                onChange: (event: { target: { value: string } }) => {
                  const selectedValue = event.target.value;
                  field.onChange(selectedValue);
                  if (selectedValue === SecurityDepositTypes.CustomizeOrRemove) {
                    trackInvite(InviteSegmentEvents.DepositCoverageSelected, {
                      property_id: address?.property_id,
                      address_id: address?.address_id,
                      value: selectedValue,
                    });
                  }
                },
              }}
              id="security-deposit-coverage"
              dataTestId="security-deposit-coverage"
              options={securityDepositOptions}
            />
          )}
        />
        {seeSecurityDepositCoverage === SecurityDepositTypes.CustomizeOrRemove && (
          <Fade in={seeSecurityDepositCoverage === SecurityDepositTypes.CustomizeOrRemove}>
            <Box>
              <Controller
                control={control}
                name="securityDepositReplacementAmount"
                render={({ field }) => (
                  <TextField
                    {...field}
                    ref={null}
                    id="security-deposit-replacement-amount"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      inputComponent: CurrencyFixedDecimalInput,
                      disableUnderline: true,
                    }}
                    slotProps={{
                      htmlInput: {
                        onWheel: (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
                      },
                    }}
                    label="Security deposit replacement amount"
                    error={!!errors.securityDepositReplacementAmount}
                    helperText={
                      (errors.securityDepositReplacementAmount &&
                        errors.securityDepositReplacementAmount?.message?.toString()) ??
                      ''
                    }
                    required
                  />
                )}
              />
              <Stack minHeight={100} px={1.75} direction="column" justifyContent={'space-between'}>
                <Typography variant="helpertext">
                  If you are allowing your renter to replace a cash deposit, please enter the dollar
                  amount.
                </Typography>
                <Typography variant="helpertext">
                  Example: If you want to collect the equivalent of 1 month security deposit, enter
                  $2,500 for a monthly rent of $2,500.
                </Typography>
              </Stack>
            </Box>
          </Fade>
        )}
      </Box>
    </>
  );
};

export default CoverageSelection;
